/**
 * Paperwork flow: Inspection → Defect → Service → Certi
 * Card background = document state; anniversary + due/work chips are separate.
 */

import { paperworkInspectionDue } from './inspectionDueDate';
import { formatDueDateLocal } from './dueDate';

function ymdToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Background for SS card from issued document chain (not overdue). */
export function paperworkFlowCardClass(row) {
  const external = !!row?.is_external;
  const type = String(row?.paperwork_type || '').toUpperCase();
  const hasService = !!row?.service_report_record_id;
  const hasDefect = type === 'DEFECT_NOTICE' || !!row?.is_defect;
  const hasCerti = type === 'FORM_12A' || type === 'S_RAD';
  const passFail = String(row?.pass_fail || '').toUpperCase();

  if (external) {
    if (hasCerti || passFail === 'PASS' || passFail === 'PASSED') {
      return 'bg-green-200 border-green-600';
    }
    if (hasDefect || passFail === 'FAIL' || passFail === 'FAILED') {
      return 'bg-yellow-100 border-yellow-500';
    }
    return 'bg-violet-50 border-violet-300';
  }

  if (hasCerti) return 'bg-green-200 border-green-600';
  if (hasService) return 'bg-blue-100 border-blue-500';
  if (hasDefect || row?.inspection_report_record_id) {
    // Defect issued (and inspection) → yellow
    if (hasDefect) return 'bg-yellow-100 border-yellow-500';
  }
  if (hasDefect) return 'bg-yellow-100 border-yellow-500';
  return 'bg-white border-slate-200';
}

/** due | overdue | '' based on next open inspection period (skips period already inspected). */
export function paperworkDueState(row) {
  const period = paperworkInspectionDue(row);
  if (!period?.date) return { state: '', date: '', term: '', label: '' };
  const today = ymdToday();
  const dueYmd = String(period.date).slice(0, 10);
  const type = String(row?.paperwork_type || '').toUpperCase();
  const passed = type === 'FORM_12A' || type === 'S_RAD';
  // Passed this visit → still show next anniversary, but no Due/Overdue chip.
  // Failed / only report → next period is the routine due (not the same quarter again).
  let state = '';
  if (!passed) {
    if (dueYmd < today) state = 'overdue';
    else state = 'due';
  }
  return {
    state,
    date: dueYmd,
    term: period.term || '',
    label: formatDueDateLocal(dueYmd) || dueYmd,
  };
}

/**
 * Work / process chip: documenting | applying | sent | completed | failed | ''
 */
export function paperworkWorkState(row) {
  const mail = String(row?.mail_status || row?.inspection_report_mail_status || '').toLowerCase();
  const job = String(row?.job_status || '').toUpperCase();
  const type = String(row?.paperwork_type || '').toUpperCase();
  const external = !!row?.is_external;

  if (type === 'FORM_12A' || type === 'S_RAD') return 'completed';
  if (type === 'DEFECT_NOTICE') return 'failed';
  if (external && (mail === 'sent' || mail === 'opened')) return 'sent';
  if (['QUERIED', 'AUCTION', 'NOTIFIED', 'PENDING'].includes(job)) return 'applying';
  if (
    ['IN_PROGRESS', 'REPAIRING', 'AFTER_PAPERWORK', 'ACCEPTED', 'SCHEDULED'].includes(job) ||
    row?.inspection_report_record_id ||
    row?.service_report_record_id
  ) {
    return 'documenting';
  }
  if (external) return 'applying';
  return '';
}

export function paperworkWorkStateLabel(state) {
  if (state === 'documenting') return 'Documenting';
  if (state === 'applying') return 'Applying';
  if (state === 'sent') return 'Sent';
  if (state === 'completed') return 'Completed';
  if (state === 'failed') return 'Failed';
  return '';
}

export function paperworkDueChipClass(state) {
  if (state === 'overdue') return 'bg-red-100 text-red-800 border border-red-300';
  if (state === 'due') return 'bg-amber-50 text-amber-900 border border-amber-300';
  return 'bg-slate-50 text-slate-600 border border-slate-200';
}

export function paperworkWorkChipClass(state) {
  if (state === 'completed') return 'bg-green-100 text-green-900 border border-green-300';
  if (state === 'failed') return 'bg-yellow-100 text-yellow-900 border border-yellow-400';
  if (state === 'sent') return 'bg-sky-100 text-sky-900 border border-sky-300';
  if (state === 'documenting') return 'bg-blue-50 text-blue-900 border border-blue-200';
  if (state === 'applying') return 'bg-violet-50 text-violet-900 border border-violet-200';
  return 'bg-slate-50 text-slate-600 border border-slate-200';
}

export const FLOW_CHAIN_ORDER = ['inspection', 'defect', 'service', 'certi'];

export function flowRoleLabel(role) {
  if (role === 'inspection') return 'Inspection report';
  if (role === 'defect') return 'Defect report';
  if (role === 'service') return 'Service report';
  if (role === 'certi') return 'Certi';
  return role;
}
